export type Frequency = "ONE_TIME" | "MONTHLY" | "QUARTERLY" | "HALF_YEARLY" | "ANNUAL";

export const RECURRING_FREQUENCIES: Array<{ id: Exclude<Frequency, "ONE_TIME">; name: string }> = [
  { id: "MONTHLY", name: "Monthly" },
  { id: "QUARTERLY", name: "Quarterly" },
  { id: "HALF_YEARLY", name: "Half-yearly" },
  { id: "ANNUAL", name: "Annual" },
];

export const MAX_FEE_AMOUNT = 10_000_000;

export type FeeHeadLite = { id: string; name: string; isOneTime: boolean };
export type StructureLite = {
  classId: string;
  feeHeadId: string;
  frequency: Frequency;
  amount: number;
};

/** One editable cell: amount is the raw text so the user can type freely. */
export type GridCellState = { amount: string; frequency: Frequency };
export type GridState = Record<string, GridCellState>;
export type GridCellPayload = {
  classId: string;
  feeHeadId: string;
  frequency: Frequency;
  amount: number | null;
};

export const cellKey = (classId: string, feeHeadId: string) => `${classId}|${feeHeadId}`;

/** The frequency a cell uses when the user types an amount without choosing one. */
export const defaultFrequency = (head: FeeHeadLite): Frequency =>
  head.isOneTime ? "ONE_TIME" : "MONTHLY";

export function buildGridState(rows: readonly StructureLite[]): GridState {
  const state: GridState = {};
  for (const r of rows) {
    state[cellKey(r.classId, r.feeHeadId)] = { amount: String(r.amount), frequency: r.frequency };
  }
  return state;
}

/** Returns an error message for a typed amount, or null when empty (means "no fee") or valid. */
export function amountInputProblem(text: string): string | null {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) return "Whole rupees only";
  const n = Number(t);
  if (n < 1) return "Must be at least 1";
  if (n > MAX_FEE_AMOUNT) return "Too large";
  return null;
}

/**
 * Cells whose amount or frequency differ from what was loaded. Clearing a cell that had a fee sends
 * `amount: null` (remove); an empty cell that never had a fee sends nothing.
 */
export function diffGrid(
  initial: GridState,
  current: GridState,
  heads: readonly FeeHeadLite[],
  classIds: readonly string[]
): GridCellPayload[] {
  const out: GridCellPayload[] = [];
  for (const classId of classIds) {
    for (const head of heads) {
      const key = cellKey(classId, head.id);
      const before = initial[key];
      const now = current[key];
      const nowAmount = (now?.amount ?? "").trim();
      const frequency = now?.frequency ?? defaultFrequency(head);

      if (nowAmount === "") {
        if (before) out.push({ classId, feeHeadId: head.id, frequency, amount: null });
        continue;
      }
      if (!before || before.amount !== nowAmount || before.frequency !== frequency) {
        out.push({ classId, feeHeadId: head.id, frequency, amount: Number(nowAmount) });
      }
    }
  }
  return out;
}

/** First problem among typed amounts (so Save can be blocked with a clear reason), or null. */
export function firstGridProblem(state: GridState): string | null {
  for (const cell of Object.values(state)) {
    const problem = amountInputProblem(cell.amount);
    if (problem) return problem;
  }
  return null;
}

/** Human summary of a save result. */
export function summarizeSave(r: {
  created: number;
  updated: number;
  removed: number;
  problems: unknown[];
}): string {
  const parts = [
    r.created ? `${r.created} added` : "",
    r.updated ? `${r.updated} changed` : "",
    r.removed ? `${r.removed} removed` : "",
  ].filter(Boolean);
  const base = parts.length ? `Fees saved: ${parts.join(", ")}.` : "No changes to save.";
  return r.problems.length ? `${base} ${r.problems.length} could not be saved.` : base;
}
